import React from 'react';
import { Calendar, MapPin, Building2, Phone, Mail, User, CheckCircle, Plus, Trash2, ChevronDown, File, Hash } from 'lucide-react';
import '../../../../styles/pages/site-visit-form.scss';
import { createSiteVisitRequest, getSiteVisitProject } from './services/siteVisitService';
import { toast } from 'react-toastify';
import i18n from '../../../../i18n';
import contryData from "../../../../constants/country.json";


const codeData = [...contryData];

const changeLanguage = (ln) => {
    i18n.changeLanguage(ln);
    window.location.reload();
};

let current_lng = localStorage.getItem('I18N_LANGUAGE');

export class SiteVisitForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            formData: {
                company_name: '',
                crNumber: '',
                civil_id: '',
                email: '',
                fullName: '',
                mobileNumber: '',
                selectedSites: [],
                civilIdFiles: null
            },
            visitors: [
                { name: '', civilId: '' }
            ],
            errors: {},
            submitted: false,
            loading: true,
            submitting: false,
            projectData: null,
            apiError: null,
            submissionData: null,
            selectedCountry: codeData[162],
            showCountryDropdown: false
        };
    }

    getLocationOptions = () => {
        const { projectData } = this.state;
        if (!projectData?.places) return [];

        return projectData.places.map(place => ({
            value: place.id,
            label: `${place.place_name}${place.address ? ` - ${place.address}` : ''}`,
            place_name: place.place_name,
            address: place.address
        }));
    };

    handleSiteSelection = (selectedOptions) => {
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                selectedSites: selectedOptions || []
            },
            errors: {
                ...prevState.errors,
                selectedSites: ''
            }
        }));
    };

    handleCheckboxChange = (e) => {
        const { value, checked } = e.target;
        const option = this.getLocationOptions().find(opt => opt.value.toString() === value);

        this.setState(prevState => {
            const currentSelected = prevState.formData.selectedSites || [];
            let newSelected;

            if (checked) {
                newSelected = [...currentSelected, option];
            } else {
                newSelected = currentSelected.filter(site => site.value.toString() !== value);
            }

            return {
                formData: {
                    ...prevState.formData,
                    selectedSites: newSelected
                },
                errors: {
                    ...prevState.errors,
                    selectedSites: ''
                }
            };
        });
    };

    componentDidMount() {
        this.fetchProjectData();
    }

    fetchProjectData = async () => {
        try {
            this.setState({ loading: true, apiError: null });

            const urlParams = new URLSearchParams(window.location.search);
            const formUrl = urlParams.get('form_url');

            if (!formUrl) {
                throw new Error('No form URL provided in the query parameters');
            }

            const response = await getSiteVisitProject(formUrl);

            if (response.success) {
                this.setState({
                    projectData: response.data,
                    loading: false
                });
            } else {
                throw new Error(response.message || 'Failed to fetch project data');
            }
        } catch (error) {
            console.error('Error fetching project data:', error);
            this.setState({
                apiError: error.message || 'Failed to load project information',
                loading: false
            });
        }
    }

    handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                [name]: type === 'checkbox' ? checked : value
            },
            errors: {
                ...prevState.errors,
                [name]: ''
            }
        }));
    }

    handleVisitorChange = (index, field, value) => {
        const visitors = [...this.state.visitors];
        visitors[index][field] = value;
        this.setState({
            visitors,
            errors: {
                ...this.state.errors,
                [`visitor_${index}_${field}`]: ''
            }
        });
    }

    addVisitor = () => {
        this.setState(prevState => ({
            visitors: [...prevState.visitors, { name: '', civilId: '' }]
        }));
    }

    removeVisitor = (index) => {
        if (this.state.visitors.length > 1) {
            this.setState(prevState => ({
                visitors: prevState.visitors.filter((_, i) => i !== index)
            }));
        }
    }

    handleFileChange = (e) => {
        const files = e.target.files;
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                civilIdFiles: files
            },
            errors: {
                ...prevState.errors,
                civilIdFiles: ''
            }
        }));
    }

    removeFile = (index) => {
        const { civilIdFiles } = this.state.formData;
        if (civilIdFiles && civilIdFiles.length > 0) {
            const updatedFiles = Array.from(civilIdFiles).filter((_, i) => i !== index);
            const dt = new DataTransfer();
            updatedFiles.forEach(file => dt.items.add(file));

            this.setState(prevState => ({
                formData: {
                    ...prevState.formData,
                    civilIdFiles: dt.files
                }
            }));
        }
    }

    validateForm = () => {
        const { formData, visitors } = this.state;
        const newErrors = {};

        if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
        if (!formData.crNumber.trim()) newErrors.crNumber = 'CR Number is required';
        if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
        if (!formData.civil_id.trim()) newErrors.civil_id = 'Resident card number is required';

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }


        if (!formData.mobileNumber.trim()) {
            newErrors.mobileNumber = 'Mobile number is required';
        } else if (!/^\d{8,15}$/.test(formData.mobileNumber.replace(/\s+/g, ''))) {
            newErrors.mobileNumber = 'Invalid mobile number';
        }

        if (!formData.selectedSites || formData.selectedSites.length === 0) {
            newErrors.selectedSites = 'Please select at least one site';
        }

        if (!formData.civilIdFiles || formData.civilIdFiles.length === 0) {
            newErrors.civilIdFiles = 'Civil ID files are required';
        }

        visitors.forEach((visitor, index) => {
            if (visitor.name.trim() || visitor.civilId.trim()) {
                if (!visitor.name.trim()) {
                    newErrors[`visitor_${index}_name`] = 'Visitor name is required when Civil ID is provided';
                }
                if (!visitor.civilId.trim()) {
                    newErrors[`visitor_${index}_civilId`] = 'Civil ID is required when name is provided';
                }
            }
        });

        this.setState({ errors: newErrors });
        return Object.keys(newErrors).length === 0;
    }

    toggleCountryDropdown = () => {
        this.setState(prevState => ({
            showCountryDropdown: !prevState.showCountryDropdown
        }));
    };

    prepareFormData = () => {
        const { formData, visitors, projectData } = this.state;

        const validVisitors = visitors.filter(visitor =>
            visitor.name.trim() && visitor.civilId.trim()
        );

        const formDataObj = new FormData();

        formDataObj.append('project_id', projectData?.id || '');
        formDataObj.append('name', formData.fullName || '');
        formDataObj.append('email', formData.email || '');
        formDataObj.append('civil_id', formData.civil_id || '');
        formDataObj.append('mobile', this.getFullPhoneNumber());
        formDataObj.append('company_number', formData.crNumber || '');
        formDataObj.append('company_name', formData.company_name || '');



        if (formData.selectedSites && formData.selectedSites.length > 0) {
            formData.selectedSites.forEach(site => {
                formDataObj.append('places[]', site.value);
            });
        }

        if (formData.civilIdFiles) {
            for (let i = 0; i < formData.civilIdFiles.length; i++) {
                formDataObj.append('files[]', formData.civilIdFiles[i]);
            }
        }

        validVisitors.forEach((visitor, index) => {
            formDataObj.append(`visitors[${index}][name]`, visitor.name);
            formDataObj.append(`visitors[${index}][civil_id]`, visitor.civilId);
        });

        return formDataObj;
    }

    handleSubmit = async (e) => {
        e.preventDefault();

        if (!this.validateForm()) {
            toast.error('Please fill the form');
            return;
        }

        this.setState({ submitting: true });

        try {
            const formPayload = this.prepareFormData();
            const response = await createSiteVisitRequest(formPayload, true);

            if (response.success || response.status) {
                toast.success(response.message || 'Site visit request submitted successfully!');
                this.setState({
                    submitted: true,
                    submissionData: response.data || {},
                    submitting: false
                });
            } else {
                toast.error(response.message || 'Form submission failed');
                this.setState({ submitting: false });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMsg);
            this.setState({ submitting: false });
        }
    }

    handleReset = () => {
        this.setState({
            formData: {
                company_name: '',
                crNumber: '',
                civil_id: '',
                email: '',
                fullName: '',
                mobileNumber: '',
                selectedSites: [],
                civilIdFiles: null
            },
            visitors: [{ name: '', civilId: '' }],
            errors: {},
            submitted: false,
            submissionData: null
        });
    }

    formatDate = (dateString) => {
        if (!dateString) return 'Date not specified';

        const options = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        return new Date(dateString).toLocaleDateString('en-US', options);
    }

    formatTimeRange = (startAt, endAt) => {
        if (!startAt || !endAt) return 'Time not specified';

        const startTime = new Date(startAt).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        const endTime = new Date(endAt).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        return `${startTime} - ${endTime}`;
    }

    renderLoadingState = () => (
        <div className="site-visit-form">
            <div className="page-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading project information...</p>
                </div>
            </div>
        </div>
    );

    renderErrorState = () => (
        <div className="site-visit-form">
            <div className="page-container">
                <div className="error-state">
                    <div className="error-icon">⚠️</div>
                    <h2>Unable to Load Form</h2>
                    <p className="error-message">{this.state.apiError}</p>
                    <button className="retry-button" onClick={this.fetchProjectData}>
                        Try Again
                    </button>
                </div>
            </div>
        </div>
    );


    handleCountrySelect = (country) => {
        this.setState({
            selectedCountry: country,
            showCountryDropdown: false
        });
    };



    handlePhoneInputChange = (e) => {
        const { value } = e.target;
        const cleanedValue = value.replace(/\D/g, '');
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                mobileNumber: cleanedValue
            },
            errors: {
                ...prevState.errors,
                mobileNumber: ''
            }
        }));
    };

    getFullPhoneNumber = () => {
        const { formData, selectedCountry } = this.state;

        if (!formData.mobileNumber?.trim()) return '';

        const cleanNumber = formData.mobileNumber.replace(/\s+/g, '');

        if (selectedCountry?.dial_code) {
            return `+${selectedCountry.dial_code.replace('+', '')}${cleanNumber}`;
        }

        return cleanNumber;
    };

    renderSuccessState = () => {
        const { submissionData } = this.state;

        return (
            <div className="site-visit-form">
                <div className="page-container">
                    <div className="success-card">
                        <div className="success-icon">
                            <CheckCircle size={64} color="#10b981" />
                        </div>
                        <h2 className="success-title">{i18n.t('registration_successful') || 'Registration Successful!'}</h2>
                        <p className="success-message">
                            {i18n.t('site_visit_success_msg') || 'You will receive a whatsapp message once your request is approved'}
                        </p>

                        {submissionData && Object.keys(submissionData).length > 0 && (
                            <div className="success-details">
                                {submissionData.id && (
                                    <div className="detail-item">
                                        <strong>{i18n.t('reference_id') || 'Reference ID'}:</strong> #{submissionData.id}
                                    </div>
                                )}
                                {submissionData.project?.project_name && (
                                    <div className="detail-item">
                                        <strong>{i18n.t('project') || 'Project'}:</strong> {submissionData.project.project_name}
                                    </div>
                                )}
                                {submissionData.company_name && (
                                    <div className="detail-item">
                                        <strong>{i18n.t('company') || 'Company'}:</strong> {submissionData.company_name}
                                    </div>
                                )}
                                {submissionData.name && (
                                    <div className="detail-item">
                                        <strong>{i18n.t('contact_person') || 'Contact Person'}:</strong> {submissionData.name}
                                    </div>
                                )}
                                {submissionData.email && (
                                    <div className="detail-item">
                                        <strong>{i18n.t('email') || 'Email'}:</strong> {submissionData.email}
                                    </div>
                                )}
                                {submissionData.mobile && (
                                    <div className="detail-item">
                                        <strong>{i18n.t('mobile') || 'Mobile'}:</strong> {submissionData.mobile}
                                    </div>
                                )}
                                {submissionData.places?.length > 0 && (
                                    <div className="detail-item">
                                        <strong>{i18n.t('selected_locations') || 'Selected Locations'}:</strong>
                                        <ul className="places-list">
                                            {submissionData.places.map(place => (
                                                <li key={place.id}>
                                                    {place.place_name}
                                                    {place.address && ` - ${place.address}`}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="success-actions">
                            <button className="reset-button" onClick={this.handleReset}>
                                {i18n.t('submit_another') || 'Submit Another Request'}
                            </button>
                            <button className="reset-button" onClick={() => window.print()}>
                                {i18n.t('print_confirmation') || 'Print Confirmation'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    renderCountryDropdown = () => {
        const { selectedCountry, showCountryDropdown } = this.state;

        return (
            <div className="country-dropdown-container">

                <div className="selected-country" onClick={this.toggleCountryDropdown}>

                    <span className="country-code">
                        {/* {selectedCountry.code} */}
                        <img
                            src={process.env.PUBLIC_URL + `/images/flags/${selectedCountry.code.toLowerCase()}.svg`}
                            alt={selectedCountry.label}
                            className="country-flag"
                        />
                        <b style={{ padding: "15px" }}>{selectedCountry.dial_code}</b>
                        <ChevronDown size={16} className={`dropdown-arrow ${showCountryDropdown ? 'rotated' : ''}`} />
                    </span>
                </div>

                {showCountryDropdown && (
                    <div className="country-dropdown-menu">
                        <div className="country-list">
                            {codeData.map((country, index) => (
                                <div
                                    key={index}
                                    className={`country-option ${selectedCountry.code === country.code ? 'selected' : ''}`}
                                    onClick={() => this.handleCountrySelect(country)}
                                >
                                    <img
                                        src={process.env.PUBLIC_URL + `/images/flags/${country.code.toLowerCase()}.svg`}
                                        alt={country.label}
                                        className="country-flag"
                                    />
                                    <span className="country-name">{country.code}</span>
                                    <span className="country-dial-code">{country.dial_code}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    renderForm = (projectData) => {
        const { formData, visitors, errors, submitting } = this.state;

        return (
            <div className="site-visit-form">
                <div className="page-container">
                    <div className="form-container">
                        <div className="left-panel">
                            <img
                                width="500"
                                height="370"
                                src={`${process.env.REACT_APP_IMAGE_URL}` + projectData?.image}
                                alt={projectData?.project_name || "Site Visit"}
                                onError={(e) => {
                                    e.target.src = '/default-project-image.jpg';
                                }}
                            />
                            <div className="details-section">
                                <h1 className="main-title">{projectData?.project_name || 'SITE VISIT'}</h1>

                                <div className="info-section">
                                    {projectData?.places?.length > 0 && (
                                        <div className="info-item">
                                            <MapPin size={20} color="#1b8f9b" />
                                            <span>{projectData.places[0].place_name}</span>
                                            {projectData.places[0].address && (
                                                <span className="address"> - {projectData.places[0].address}</span>
                                            )}
                                        </div>
                                    )}

                                    <div className="info-item">
                                        <Calendar size={20} color="#1b8f9b" />
                                        <span>
                                            {this.formatTimeRange(projectData?.start_at, projectData?.end_at)}, {this.formatDate(projectData?.start_at)}
                                        </span>
                                    </div>
                                </div>

                                <div className="instructions">
                                    <p className="instruction-text">
                                        {projectData?.description || 'Please choose a suitable date and time to visit the site.'}
                                    </p>
                                    <p className="instruction-text">
                                        Visits require prior confirmation and compliance with safety instructions.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="right-panel">
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <h2 className="form-title">{i18n.t('enter_details')}</h2>

                                <div>
                                    <div className="header-lang" style={{ cursor: 'pointer' }}>
                                        {current_lng === 'ar' && (
                                            <a href="#" style={{ color: "black" }} onClick={(e) => { e.preventDefault(); changeLanguage('en'); }}>
                                                <img
                                                    src={process.env.PUBLIC_URL + '/assets/images/flags/en.svg'}
                                                    height="20"
                                                    alt=""
                                                />{' '}
                                                EN{' '}
                                            </a>
                                        )}
                                        {current_lng === 'en' && (
                                            <a href="#" style={{ color: "black" }} onClick={(e) => { e.preventDefault(); changeLanguage('ar'); }}>
                                                <img
                                                    src={process.env.PUBLIC_URL + '/assets/images/flags/ar.svg'}
                                                    height="20"
                                                    alt=""
                                                />{' '}
                                                عربي{' '}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={this.handleSubmit} className="registration-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">
                                            <Building2 size={16} />
                                            {i18n.t('company_name')} *
                                        </label>
                                        <input
                                            type="text"
                                            name="company_name"
                                            value={formData.company_name}
                                            onChange={this.handleInputChange}
                                            className={`form-input ${errors.company_name ? 'input-error' : ''}`}
                                            placeholder={i18n.t('enter_company_name')}
                                            disabled={submitting}

                                        />
                                        {errors.company_name && <span className="error-text">{errors.company_name}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            <Hash size={16} />
                                            {i18n.t('cr_number')} *
                                        </label>
                                        <input
                                            type="text"
                                            name="crNumber"
                                            value={formData.crNumber}
                                            onChange={this.handleInputChange}
                                            className={`form-input ${errors.crNumber ? 'input-error' : ''}`}
                                            placeholder={i18n.t('enter_cr_number')}
                                            disabled={submitting}

                                        />
                                        {errors.crNumber && <span className="error-text">{errors.crNumber}</span>}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">
                                            <User size={16} />
                                            {i18n.t('your_full_name')}  *
                                        </label>
                                        <input
                                            type="text"
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={this.handleInputChange}
                                            className={`form-input ${errors.fullName ? 'input-error' : ''}`}
                                            placeholder={i18n.t('enter_your_full_name')}
                                            disabled={submitting}


                                        />
                                        {errors.fullName && <span className="error-text">{errors.fullName}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            <User size={16} />
                                            {i18n.t('civil_id_number')} *
                                        </label>
                                        <input
                                            type="text"
                                            name="civil_id"
                                            value={formData.civil_id}
                                            onChange={this.handleInputChange}
                                            className={`form-input ${errors.civil_id ? 'input-error' : ''}`}
                                            placeholder={i18n.t('enter_resident_card_number')}
                                            disabled={submitting}


                                        />
                                        {errors.civil_id && <span className="error-text">{errors.civil_id}</span>}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">
                                            <Mail size={16} />
                                            {i18n.t('email')}  *
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={this.handleInputChange}
                                            className={`form-input ${errors.email ? 'input-error' : ''}`}
                                            placeholder={i18n.t('enter_email')}
                                            disabled={submitting}

                                        />
                                        {errors.email && <span className="error-text">{errors.email}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            <Phone size={16} />
                                            {i18n.t('mobile')} *
                                        </label>

                                        <div className="phone-input-container">
                                            <div className="phone-input-wrapper">
                                                {this.renderCountryDropdown()}

                                                <div className="phone-number-input">
                                                    <input
                                                        type="tel"
                                                        name="mobileNumber"
                                                        value={formData.mobileNumber}
                                                        onChange={this.handlePhoneInputChange}
                                                        className={`phone-input ${errors.mobileNumber ? 'input-error' : ''}`}
                                                        placeholder={i18n.t('enter_mobile')}
                                                        disabled={submitting}

                                                    />
                                                </div>
                                            </div>
                                        </div>


                                        {errors.mobileNumber && <span className="error-text">{errors.mobileNumber}</span>}
                                    </div>
                                </div>

                                {/* Site Selection */}
                                <div className="form-group">
                                    <label className="form-label">
                                        <MapPin size={16} />
                                        {i18n.t('location')} *
                                    </label>
                                    <div className={`custom-multi-select ${errors.selectedSites ? 'select-error' : ''}`}>
                                        {this.getLocationOptions().map(option => (
                                            <label key={option.value} className="checkbox-option">
                                                <input
                                                    type="checkbox"
                                                    value={option.value}
                                                    checked={formData.selectedSites?.some(site => site.value === option.value)}
                                                    onChange={this.handleCheckboxChange}
                                                    disabled={submitting}


                                                />
                                                <span className="checkmark"></span>
                                                <span className="option-label">
                                                    <strong>{option.place_name}</strong>
                                                    {option.address && <span className="option-address"> - {option.address}</span>}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    {errors.selectedSites && <span className="error-text">{errors.selectedSites}</span>}
                                </div>

                                {/* File Upload - REQUIRED */}
                                <div className="form-group">
                                    <label className="form-label">
                                        <File size={16} />
                                        {i18n.t('civil_id_files')} *
                                    </label>
                                    <div className="file-upload-container">
                                        <div className="file-upload-notice">
                                            <div className="notice-icon">ℹ️</div>
                                            <div className="notice-content">
                                                <strong>{i18n.t('important')}:</strong> {i18n.t('upload_civil_id_instruction')}
                                                <br />
                                                <small>{i18n.t('accepted_formats')}</small>
                                            </div>
                                        </div>
                                        <div className="file-drop-zone">
                                            <input
                                                type="file"
                                                name="civilIdFiles"
                                                multiple
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={this.handleFileChange}
                                                className="file-input-hidden"
                                                id="civilIdFiles"
                                                disabled={submitting}


                                            />
                                            <label htmlFor="civilIdFiles" className="file-drop-label">
                                                <File size={32} color="#1b8f9b" />
                                                <span className="file-drop-text">
                                                    {i18n.t('browse_or_drag')}
                                                </span>
                                                <span className="file-drop-hint">
                                                    {i18n.t('upload_all_civil_ids')}
                                                </span>
                                            </label>
                                        </div>
                                        {formData.civilIdFiles && formData.civilIdFiles.length > 0 && (
                                            <div className="uploaded-files-list">
                                                {Array.from(formData.civilIdFiles).map((file, index) => (
                                                    <div key={index} className="uploaded-file-item">
                                                        <File size={16} />
                                                        <span className="file-name">{file.name}</span>
                                                        <span className="file-size">({(file.size / 1024).toFixed(1)} KB)</span>
                                                        <button
                                                            type="button"
                                                            className="remove-file-btn"
                                                            onClick={() => this.removeFile(index)}
                                                            disabled={submitting}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {errors.civilIdFiles && <span className="error-text">{errors.civilIdFiles}</span>}
                                    </div>
                                </div>

                                {/* Visitors Section - OPTIONAL */}
                                <div className="visitors-section">
                                    <div className="visitors-header">
                                        <h3 className="visitors-title">{i18n.t('visitor_details')} (Optional)</h3>
                                        <button
                                            type="button"
                                            className="add-visitor-btn"
                                            onClick={this.addVisitor}
                                            disabled={submitting}
                                        >
                                            <Plus size={18} />
                                            {i18n.t('add_visitor')}
                                        </button>
                                    </div>

                                    {visitors.map((visitor, index) => (
                                        <div key={index} className="visitor-card">
                                            <div className="visitor-card-header">
                                                <span className="visitor-number">{i18n.t('visitor')} {index + 1}</span>
                                                {visitors.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className="remove-visitor-btn"
                                                        onClick={() => this.removeVisitor(index)}
                                                        disabled={submitting}
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        <User size={16} />
                                                        {i18n.t('visitor_name')}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={visitor.name}
                                                        onChange={(e) => this.handleVisitorChange(index, 'name', e.target.value)}
                                                        className={`form-input ${errors[`visitor_${index}_name`] ? 'input-error' : ''}`}
                                                        placeholder={i18n.t('enter_visitor_name')}
                                                        disabled={submitting}
                                                    />
                                                    {errors[`visitor_${index}_name`] && (
                                                        <span className="error-text">{errors[`visitor_${index}_name`]}</span>
                                                    )}
                                                </div>

                                                <div className="form-group">
                                                    <label className="form-label">
                                                        <User size={16} />
                                                        {i18n.t('civil_id_number')}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={visitor.civilId}
                                                        onChange={(e) => this.handleVisitorChange(index, 'civilId', e.target.value)}
                                                        className={`form-input ${errors[`visitor_${index}_civilId`] ? 'input-error' : ''}`}
                                                        placeholder={i18n.t('enter_visitor_civil')}
                                                        disabled={submitting}
                                                    />
                                                    {errors[`visitor_${index}_civilId`] && (
                                                        <span className="error-text">{errors[`visitor_${index}_civilId`]}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    type="submit"
                                    className="submit-button"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <div className="submit-spinner"></div>
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit Site Visit Request'
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { loading, apiError, submitted } = this.state;

        if (loading) {
            return this.renderLoadingState();
        }

        if (apiError) {
            return this.renderErrorState();
        }

        if (submitted) {
            return this.renderSuccessState();
        }

        return this.renderForm(this.state.projectData);
    }
}

